import Axios from "axios";
import { AI_AGENT_BASE_URL } from "../config";
import {
  getLivisToken,
  getLivisUserInfo,
  getLivisLanguage,
} from "../helpers/storage";

function resolveIndustry(): string {
  try {
    const hostname = window.location.hostname || "";
    const isLocalhost =
      hostname === "localhost" ||
      /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname) ||
      hostname === "livis.ai" ||
      hostname === "www.livis.ai";
    if (isLocalhost) return "none";
    if (hostname.includes("automotive")) return "automotive";
    if (hostname.includes("aerospace")) return "aerospace";
    if (hostname.includes("electronics")) return "electronics";
    if (hostname.includes("semi")) return "semiconductor";
    return "none";
  } catch {
    return "none";
  }
}

function resolveUserContext(): { permissions: string; is_superuser: string } {
  try {
    const raw = getLivisUserInfo();
    if (!raw) return { permissions: "", is_superuser: "0" };
    const info: any = typeof raw === "string" ? JSON.parse(raw) : raw;
    const perms = Array.isArray(info?.user_permissions)
      ? info.user_permissions.join(",")
      : "";
    const isSuper = info?.is_superuser ? "1" : "0";
    return { permissions: perms, is_superuser: isSuper };
  } catch {
    return { permissions: "", is_superuser: "0" };
  }
}

export function resolveUserEmail(): string {
  try {
    const raw = getLivisUserInfo();
    if (!raw) return "";
    const info: any = typeof raw === "string" ? JSON.parse(raw) : raw;
    return info?.email || "";
  } catch {
    return "";
  }
}

const aiAgentAxios = Axios.create({
  baseURL: AI_AGENT_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

aiAgentAxios.interceptors.request.use((config) => {
  const token = getLivisToken();
  config.headers = config.headers || {};
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  const { permissions, is_superuser } = resolveUserContext();
  (config.headers as any)["X-Livis-Permissions"] = permissions;
  (config.headers as any)["X-Livis-Is-Superuser"] = is_superuser;
  (config.headers as any)["X-Livis-Industry"] = resolveIndustry();
  (config.headers as any)["X-Livis-Language"] = getLivisLanguage() || "en";
  (config.headers as any)["X-User-Email"] = resolveUserEmail();
  return config;
});

export const getAgentSessions = (offset = 0, limit = 100): Promise<any> => {
  return aiAgentAxios.get("/agent/sessions", { params: { offset, limit } });
};

export const getAgentSession = (
  sessionId: string,
  offset = 0,
  limit = 500
): Promise<any> => {
  return aiAgentAxios.get(`/agent/sessions/${sessionId}`, {
    params: { offset, limit },
  });
};

export const createAgentSession = (title: string): Promise<any> => {
  return aiAgentAxios.post("/agent/sessions", { title });
};

export const renameAgentSession = (
  sessionId: string,
  title: string
): Promise<any> => {
  return aiAgentAxios.patch(`/agent/sessions/${sessionId}`, { title });
};

export const deleteAgentSession = (sessionId: string): Promise<any> => {
  return aiAgentAxios.delete(`/agent/sessions/${sessionId}`);
};

export const askAgent = (
  sessionId: string,
  userQuery: string,
  mode: 'agent' | 'ask' = 'agent',
  signal?: AbortSignal,
  options?: { regenerate?: boolean; ui_context?: Record<string, unknown>; mentions?: Array<{ type: string; id: string; label: string }> },
): Promise<any> => {
  const body: Record<string, unknown> = {
    session_id: sessionId,
    user_query: userQuery,
    mode,
  };
  if (options?.mentions?.length) body.mentions = options.mentions;
  if (options?.regenerate) body.regenerate = true;
  if (options?.ui_context) body.ui_context = options.ui_context;
  return aiAgentAxios.post("/agent/ask", body, { signal });
};

export const sendAgentEvent = (
  sessionId: string,
  markerId: string | null,
  action: string,
  value: any,
  signal?: AbortSignal
): Promise<any> => {
  return aiAgentAxios.post(
    "/agent/event",
    { session_id: sessionId, marker_id: markerId, action, value },
    { signal }
  );
};

export const clearAgentChat = (sessionId: string): Promise<any> => {
  return aiAgentAxios.post("/agent/clear", { session_id: sessionId });
};


export const postAgentFeedback = (
  sessionId: string,
  rating: 'up' | 'down',
  messageId?: string,
  comment = '',
): Promise<any> => {
  return aiAgentAxios.post('/agent/feedback', {
    session_id: sessionId,
    message_id: messageId,
    rating,
    comment,
  });
};


export async function uploadAgentFile(
  sessionId: string,
  kind: string,
  context: Record<string, unknown>,
  file: File,
): Promise<{ file_ref: string }> {
  const fd = new FormData();
  fd.append('session_id', sessionId);
  fd.append('kind', kind);
  fd.append('context', JSON.stringify(context || {}));
  fd.append('file', file);
  const resp = await aiAgentAxios.post('/agent/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const json = resp?.data;
  return json?.data || { file_ref: '' };
}
